"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import {
  ArrowDownIcon,
  GlobeIcon,
  PinAltIcon,
  SmartPhone01Icon,
  UserIcon,
} from "@/components/icons-lib";
import { SCHOOL_COLORS } from "@/providers/maps/constants";
import type { IFindMySchoolSchoolItem } from "@/providers/store/api/find-my-school/find-my-school.interface";
import { cn } from "@/shared/helpers/cn.helper";
import { formatFindMySchoolGradeRange } from "@/shared/utils/find-my-school-grade-display.utils";


export interface SchoolListItemProps {
  school: IFindMySchoolSchoolItem;
  isHighlighted?: boolean;
  variant?: "desktop" | "mobile";
  onClick?: (school: IFindMySchoolSchoolItem) => void;
}

interface LevelBadge {
  type: string;
  short: string;
  color: string;
}

const LEVEL_BADGE: Record<string, Omit<LevelBadge, "type">> = {
  ELEMENTARY: { short: "ES", color: SCHOOL_COLORS.ELEMENTARY },
  MIDDLE: { short: "MS", color: SCHOOL_COLORS.MIDDLE },
  HIGH: { short: "HS", color: SCHOOL_COLORS.HIGH },
  K8: { short: "K8", color: SCHOOL_COLORS.K8 },
};

function formatShortAddress(address: string): string {
  // Input is typically: "street, city, ST ZIP". We want: "street, city, ST"
  const parts = address.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return address;
  const state = parts[2]?.split(" ")?.[0] ?? parts[2];
  return [parts[0], parts[1], state].filter(Boolean).join(", ");
}

function formatWebsiteLabel(url: string): string {
  return url.replace(/^https?:\/\//i, "").replace(/\/$/, "");
}

function SchoolBadges({ badges }: { badges: LevelBadge[] }) {
  return (
    <>
      {badges.map(({ type, short, color }) => (
        <span
          key={type}
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[12px]/[16px] font-medium text-white"
          style={{ backgroundColor: color }}
        >
          {short}
        </span>
      ))}
    </>
  );
}

function MobileInfoRow({ label, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <div className="flex items-start justify-between gap-3 text-[13px]/[18px]">
      <span className="shrink-0 text-[#6B6667]">{label}</span>
      <div className="min-w-0 text-right font-medium text-[#161515]">{children}</div>
    </div>
  );
}

function MobileSchoolDetails({ school }: { school: IFindMySchoolSchoolItem }) {
  const hasDetails = Boolean(school.principalName ?? school.phone ?? school.url);
  if (!hasDetails) return null;

  return (
    <div className="flex flex-col gap-2 border-t border-[#EDECEB] pt-2.5">
      {school.principalName && (
        <MobileInfoRow label="Principal">{school.principalName}</MobileInfoRow>
      )}
      {school.phone && <MobileInfoRow label="Phone">{school.phone}</MobileInfoRow>}
      {school.url && (
        <MobileInfoRow label="Website">
          <a
            className="break-words font-semibold text-[#520313] hover:underline"
            href={school.url}
            rel="noreferrer"
            target="_blank"
            onClick={(event) => event.stopPropagation()}
          >
            {formatWebsiteLabel(school.url)}
          </a>
        </MobileInfoRow>
      )}
    </div>
  );
}

interface MobileSchoolContentProps {
  school: IFindMySchoolSchoolItem;
  badges: LevelBadge[];
  gradeRange: string | null;
  shortAddress: string | null;
  selected: boolean;
}

function MobileSchoolContent({
  school,
  badges,
  gradeRange,
  shortAddress,
  selected,
}: MobileSchoolContentProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      <div className="flex min-w-0 items-start justify-between gap-2.5">
        <div className="min-w-0 text-[16px]/[22px] font-bold text-[#161515]">
          {school.name ?? "Unknown School"}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          <SchoolBadges badges={badges} />
        </div>
      </div>

      {shortAddress && (
        <div className="flex min-w-0 items-start gap-2 text-[14px]/[20px] font-normal text-[#4B4849]">
          <PinAltIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#520313]" />
          <span className="min-w-0 break-words">{shortAddress}</span>
        </div>
      )}

      {gradeRange && (
        <span className="inline-flex w-fit items-center rounded-full bg-[#F3F3F3] px-2 py-0.5 text-[12px]/[16px] font-medium text-[#161515]">
          {gradeRange}
        </span>
      )}

      {!selected && (
        <div className="flex items-center gap-1 text-[12px]/[16px] font-semibold text-[#8A8586]">
          <span>More info</span>
          <ArrowDownIcon className="h-3.5 w-3.5" />
        </div>
      )}

      {selected && <MobileSchoolDetails school={school} />}
    </div>
  );
}

interface DesktopSchoolContentProps {
  school: IFindMySchoolSchoolItem;
  badges: LevelBadge[];
  gradeRange: string | null;
  shortAddress: string | null;
  infoOpen: boolean;
  onInfoOpenChange: (open: boolean) => void;
}

function DesktopSchoolContent({
  school,
  badges,
  gradeRange,
  shortAddress,
  infoOpen,
  onInfoOpenChange,
}: DesktopSchoolContentProps) {
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <div className="flex min-w-0 flex-col gap-2">
        <div className="text-[16px]/[24px] font-bold text-[#161515]">
          {school.name ?? "Unknown School"}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SchoolBadges badges={badges} />
          {gradeRange && (
            <span className="inline-flex items-center rounded-full bg-[#F3F3F3] px-2 py-0.5 text-[12px]/[16px] font-medium text-[#161515]">
              {gradeRange}
            </span>
          )}
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-3">
        {shortAddress && (
          <div className="flex min-w-0 items-start gap-2 text-[14px]/[20px] font-normal text-[#161515]">
            <PinAltIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#520313]" />
            <span className="min-w-0 break-words">{shortAddress}</span>
          </div>
        )}

        {!infoOpen ? (
          <button
            className="w-fit text-[14px]/[20px] font-medium text-[#520313] hover:underline"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onInfoOpenChange(true);
            }}
          >
            Show school info
          </button>
        ) : (
          <div className="flex flex-col gap-2">
            {school.principalName && (
              <div className="flex items-start gap-2 text-[14px]/[20px] font-normal text-[#161515]">
                <UserIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#520313]" />
                <span className="min-w-0 break-words">{school.principalName}</span>
              </div>
            )}

            {school.phone && (
              <div className="flex items-start gap-2 text-[14px]/[20px] font-normal text-[#161515]">
                <SmartPhone01Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#520313]" />
                <span className="min-w-0 break-words">{school.phone}</span>
              </div>
            )}

            {school.url && (
              <div className="flex items-start gap-2 text-[14px]/[20px] font-normal text-[#161515]">
                <GlobeIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#520313]" />
                <a
                  className="min-w-0 break-words text-[#520313] hover:underline"
                  href={school.url}
                  rel="noreferrer"
                  target="_blank"
                  onClick={(event) => event.stopPropagation()}
                >
                  {school.url}
                </a>
              </div>
            )}

            <button
              className="w-fit text-[14px]/[20px] font-medium text-[#520313] hover:underline"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onInfoOpenChange(false);
              }}
            >
              Hide school info
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function SchoolListItem({
  school,
  isHighlighted = false,
  variant = "desktop",
  onClick,
}: SchoolListItemProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isHighlighted || !itemRef.current) return;
    const el = itemRef.current;
    const id = requestAnimationFrame(() => {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [isHighlighted]);

  const levelBadges = useMemo(() => {
    const badges: LevelBadge[] = [];
    for (const schoolType of school.schoolTypes) {
      const badge = LEVEL_BADGE[schoolType];
      if (badge) badges.push({ type: schoolType, short: badge.short, color: badge.color });
    }
    return badges;
  }, [school.schoolTypes]);

  const gradeRange = useMemo(
    () => formatFindMySchoolGradeRange(school.lowestGrade, school.highestGrade),
    [school.lowestGrade, school.highestGrade],
  );

  const shortAddress = useMemo(() => {
    if (!school.address) return null;
    return formatShortAddress(school.address);
  }, [school.address]);

  const handleClick = () => {
    onClick?.(school);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleClick();
  };

  const mobile = variant === "mobile";

  return (
    <div
      ref={itemRef}
      className={cn(
        "cursor-pointer rounded-[12px] bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#520313] focus:ring-offset-2",
        mobile ? "p-3.5" : "p-4",
        isHighlighted
          ? "border-[1.5px] border-[#520313]"
          : "border border-[#DFDFDF] hover:border-[#520313]/40",
      )}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {mobile ? (
        <MobileSchoolContent
          badges={levelBadges}
          gradeRange={gradeRange}
          school={school}
          selected={isHighlighted}
          shortAddress={shortAddress}
        />
      ) : (
        <DesktopSchoolContent
          badges={levelBadges}
          gradeRange={gradeRange}
          infoOpen={infoOpen}
          school={school}
          shortAddress={shortAddress}
          onInfoOpenChange={setInfoOpen}
        />
      )}
    </div>
  );
}
